import React from 'react';
import { ChevronRight, ChevronLeft, Clock, Star, Sparkles } from 'lucide-react';
import SectionHeader from './SectionHeader';
import { isCurrentPeriod } from './utils';
import { FREE_KUNDLI_TRANSLATIONS } from '../../../../languages/freeKundli';
import { getPlanetName } from '../../../../utils/planetNames';
import {
    SunPlanetIcon,
    MoonPlanetIcon,
    MarsPlanetIcon,
    MercuryPlanetIcon,
    JupiterPlanetIcon,
    VenusPlanetIcon,
    SaturnPlanetIcon,
    RahuPlanetIcon,
    KetuPlanetIcon
} from './AstroIcons';

interface DashaTabProps {
    kundliData: any;
    viewDasha: any[];
    setViewDasha: (dasha: any[]) => void;
    language: 'english' | 'hindi';
}

const DashaTab: React.FC<DashaTabProps> = ({ kundliData, viewDasha, setViewDasha, language }) => {
    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = FREE_KUNDLI_TRANSLATIONS[langKey];

    // Helper function to get planet icon
    const getPlanetIcon = (name: string, className: string = "w-6 h-6") => {
        const lowerName = name.toLowerCase();
        if (lowerName.includes('sun') || lowerName.includes('surya')) return <SunPlanetIcon className={className} />;
        if (lowerName.includes('moon') || lowerName.includes('chandra')) return <MoonPlanetIcon className={className} />;
        if (lowerName.includes('mars') || lowerName.includes('mangal')) return <MarsPlanetIcon className={className} />;
        if (lowerName.includes('mercury') || lowerName.includes('budh')) return <MercuryPlanetIcon className={className} />;
        if (lowerName.includes('jupiter') || lowerName.includes('guru') || lowerName.includes('brihaspati')) return <JupiterPlanetIcon className={className} />;
        if (lowerName.includes('venus') || lowerName.includes('shukra')) return <VenusPlanetIcon className={className} />;
        if (lowerName.includes('saturn') || lowerName.includes('shani')) return <SaturnPlanetIcon className={className} />;
        if (lowerName.includes('rahu') || lowerName.includes('north node')) return <RahuPlanetIcon className={className} />;
        if (lowerName.includes('ketu') || lowerName.includes('south node')) return <KetuPlanetIcon className={className} />;
        return <Star size={24} className="text-primary-400" />;
    };

    return (
        <div className="space-y-8 animate-in slide-in-from-right-8 duration-500 font-sans">
            <div className="bg-white/[0.08] border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-8">
                <div className="flex items-center justify-between mb-8">
                    <SectionHeader icon={Clock} title={t.vimshottariDasha} description={t.dashaDesc} className="mb-0" />

                    {viewDasha.length > 0 && (
                        <button
                            onClick={() => setViewDasha(viewDasha.slice(0, -1))}
                            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-sm font-bold text-white transition-colors flex items-center gap-2"
                        >
                            <ChevronLeft size={16} />
                            {t.backTo} {viewDasha.length === 1 ? t.mahadasha : t.antardasha}
                        </button>
                    )}
                </div>

                {/* Breadcrumbs */}
                <div className="flex items-center gap-2 text-sm text-primary-200/60 overflow-x-auto pb-4 mb-4">
                    <span className={viewDasha.length === 0 ? "text-white font-bold cursor-pointer" : "cursor-pointer hover:text-white transition-colors"} onClick={() => setViewDasha([])}>{t.mahadasha}</span>
                    {viewDasha.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                            <ChevronRight size={14} />
                            <span
                                className={`${idx === viewDasha.length - 1 ? "text-white font-bold" : "cursor-pointer hover:text-white transition-colors"}`}
                                onClick={() => setViewDasha(viewDasha.slice(0, idx + 1))}
                            >
                                {getPlanetName(item.planet_name || item.planet, t.planetNames)}
                            </span>
                        </div>
                    ))}
                    {viewDasha.length === 1 && <div className="flex items-center gap-2"><ChevronRight size={14} /> <span>{t.antardasha}</span></div>}
                    {viewDasha.length === 2 && <div className="flex items-center gap-2"><ChevronRight size={14} /> <span>{t.pratyantardasha}</span></div>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {(
                        viewDasha.length === 0
                            ? kundliData.vimshottari_dasha?.mahadasha_sequence
                            : viewDasha.length === 1
                                ? viewDasha[0].antardasha
                                : viewDasha[1].pratyantardasha
                    )?.map((m: any, idx: number) => {
                        // Use date-based current period check
                        const isCurrent = isCurrentPeriod(m.start_date, m.end_date);

                        return (
                            <div
                                key={idx}
                                onClick={() => {
                                    if (viewDasha.length < 2) {
                                        setViewDasha([...viewDasha, m]);
                                    }
                                }}
                                className={`relative p-5 md:p-6 rounded-[24px] md:rounded-[32px] border transition-colors duration-300 group cursor-pointer ${isCurrent
                                    ? 'bg-violet-600/20 border-violet-500 shadow-lg shadow-violet-900/30'
                                    : 'bg-white/[0.08] border-white/20 hover:border-white/20 hover:bg-white/10'
                                    }`}
                            >
                                {isCurrent && (
                                    <div className="absolute -top-3 left-6 px-3 py-1 bg-gradient-to-r from-violet-500 to-purple-500 text-[10px] font-black uppercase tracking-widest rounded-full text-white shadow-lg flex items-center gap-1.5">
                                        <Sparkles size={12} />
                                        {t.currPeriod}
                                    </div>
                                )}
                                <div className="flex justify-between items-start mb-4">
                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform ${isCurrent ? 'bg-violet-500/20' : 'bg-white/10'}`}>
                                        {getPlanetIcon(m.planet_name || m.planet, "w-7 h-7")}
                                    </div>
                                    {viewDasha.length < 2 && (
                                        <div className="w-8 h-8 rounded-full bg-white/[0.08] flex items-center justify-center text-primary-300 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <ChevronRight size={16} />
                                        </div>
                                    )}
                                </div>
                                <h4 className={`text-xl font-black mb-4 ${isCurrent ? 'text-violet-300' : 'text-white'}`}>{getPlanetName(m.planet_name || m.planet, t.planetNames)}</h4>
                                <div className="space-y-2 text-sm text-primary-200/60">
                                    <div className="flex justify-between">
                                        <span>{t.starts}</span>
                                        <span className="text-white font-medium">{m.start_date}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>{t.ends}</span>
                                        <span className="text-white font-medium">{m.end_date}</span>
                                    </div>
                                    {m.duration_years && (
                                        <div className="flex justify-between">
                                            <span>{t.duration}</span>
                                            <span className={`font-bold ${isCurrent ? 'text-violet-400' : 'text-primary-400'}`}>{m.duration_years} {t.years}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default DashaTab;
